import { useState } from "react";
import { Send, Building2, User, Mail, Phone, MapPin, Globe, MessageSquare } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const ApplicationSection = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    hotel_name: "",
    contact_name: "",
    email: "",
    phone: "",
    location: "",
    country: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const success = await onSubmit(formData);
    
    if (success) {
      setSubmitted(true);
      setFormData({
        hotel_name: "",
        contact_name: "",
        email: "",
        phone: "",
        location: "",
        country: "",
        message: ""
      });
    }
    
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <section 
        id="apply" 
        className="py-20 lg:py-32 bg-[#0B132B]"
        data-testid="application-section"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-12 rounded-lg">
            <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
              <Send className="w-10 h-10 text-[#0B132B]" />
            </div>
            <h3 className="font-heading text-3xl font-bold text-white mb-4">
              Application Submitted!
            </h3>
            <p className="text-white/80 mb-8">
              Thank you for applying for WTTQ certification. Our team will review your application 
              and contact you within 5-7 business days.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="bg-[#D4AF37] text-[#0B132B] px-8 py-3 font-semibold hover:bg-[#E8D38E] transition-colors"
              data-testid="submit-another-button"
            >
              Submit Another Application
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section 
      id="apply" 
      className="py-20 lg:py-32 bg-[#0B132B]"
      data-testid="application-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div>
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-semibold mb-4">
              Apply for Certification
            </p>
            <h2 
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight"
              data-testid="application-title"
            >
              Get Your Quality Label Mark
            </h2>
            <p className="text-white/80 text-lg mb-8 leading-relaxed">
              Join the elite group of hospitality businesses recognized for their commitment 
              to excellence. Apply for WTTQ certification today and stand out in the global 
              travel and tourism industry.
            </p>

            {/* Process Steps */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0 text-[#0B132B] font-bold text-sm">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Submit Application</h4>
                  <p className="text-white/60 text-sm">Complete the form with your property details</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0 text-[#0B132B] font-bold text-sm">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Quality Assessment</h4>
                  <p className="text-white/60 text-sm">Our experts evaluate your establishment</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0 text-[#0B132B] font-bold text-sm">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Receive Certification</h4>
                  <p className="text-white/60 text-sm">Earn your Gold, Silver, or Bronze quality mark</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-white p-8 lg:p-10">
            <h3 className="font-heading text-2xl font-bold text-[#0B132B] mb-6">
              Certification Application
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="application-form">
              {/* Hotel Name */}
              <div>
                <Label htmlFor="hotel_name" className="text-[#0B132B] font-medium mb-2 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#718096]" />
                  Hotel / Property Name
                </Label>
                <Input
                  id="hotel_name"
                  name="hotel_name"
                  value={formData.hotel_name}
                  onChange={handleChange}
                  placeholder="Enter property name"
                  required
                  className="h-12 border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                  data-testid="input-hotel-name"
                />
              </div>

              {/* Contact Name */}
              <div>
                <Label htmlFor="contact_name" className="text-[#0B132B] font-medium mb-2 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#718096]" />
                  Contact Person
                </Label>
                <Input
                  id="contact_name"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  placeholder="Full name"
                  required
                  className="h-12 border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                  data-testid="input-contact-name"
                />
              </div>

              {/* Email & Phone Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-[#0B132B] font-medium mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#718096]" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    required
                    className="h-12 border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#0B132B] font-medium mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#718096]" />
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                    required
                    className="h-12 border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {/* Location & Country Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location" className="text-[#0B132B] font-medium mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#718096]" />
                    City / Location
                  </Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="City name"
                    required
                    className="h-12 border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="input-location"
                  />
                </div>
                <div>
                  <Label htmlFor="country" className="text-[#0B132B] font-medium mb-2 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-[#718096]" />
                    Country
                  </Label>
                  <Input
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Country"
                    required
                    className="h-12 border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="input-country"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-[#0B132B] font-medium mb-2 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#718096]" />
                  Tell us about your property
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe your property, amenities, and why you believe it deserves WTTQ certification..."
                  rows={4}
                  required
                  className="border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] resize-none"
                  data-testid="input-message"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0B132B] text-white py-4 font-semibold tracking-wide hover:bg-[#1C2541] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="submit-application-button"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Application
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApplicationSection;
